import { type TUiSidebarSection } from '@umodeapporg/ui/components/Shell/SidebarMenu';
import { type SymbolCodepoints } from '@umodeapporg/ui/components/UI/MaterialSymbol';

/**
 * The menu in GROUPS, which is the list this product edits when a screen is born or moves.
 *
 * The order here IS the order on screen, and it reads as the day does: what to look at
 * first, what is operated every day, what is planned and what is managed. What is set up
 * once (Cadastros, Configurações) left the groups for the FOOT of the sidebar, next to the
 * manual: those are opened on purpose, not while working the day. A screen moves between
 * groups by changing one line.
 */

export interface INavigationEntry {
  icon: SymbolCodepoints;
  /** Key in `common.json`, resolved by the hook: this list stays free of React. */
  textKey: string;
  to: string;
  /** Only the root needs it: without `end` every route would match "/". */
  end?: boolean;
}

export interface INavigationGroup {
  id: string;
  /** The small heading above the group, or `null` for the leading group, which has none. */
  labelKey: string | null;
  /**
   * Which of the library's named groups renders this one. The sidebar of `@umodeapporg/ui`
   * (0.22.1) heads exactly three (`favorites`, `management`, `registration`) in a fixed
   * order, plus one leading group with no heading, and each heading reads ONE global key
   * (`ui:sidebar.sections.*`, renamed in `I18nManager`) that the footer menu shares. So
   * three headings is the whole budget, and it is spent on the day's work: a fourth group
   * would either repeat a heading or, with a name the library does not know, drop its rows
   * from the menu. That is why the setup screens sit in `NAVIGATION_FOOTER` instead.
   *
   * `favorites` is the star button's group, and it is free here because this app never
   * turns the star on: `configureUiPreferences` in `app/providers.tsx` is called without
   * `resolveScopeId`, so the library's `canFavorite` is false, no row shows a star and the
   * group can never receive one.
   */
  section?: TUiSidebarSection;
  items: ReadonlyArray<INavigationEntry>;
}

export const NAVIGATION_GROUPS: ReadonlyArray<INavigationGroup> = [
  {
    id: 'inicio',
    labelKey: null,
    items: [{ icon: 'home', textKey: 'nav.home', to: '/', end: true }],
  },
  {
    id: 'diaADia',
    labelKey: 'nav.grupoDiaADia',
    section: 'favorites',
    items: [
      { icon: 'notification_important', textKey: 'nav.fila', to: '/fila' },
      { icon: 'schedule', textKey: 'nav.apontamento', to: '/apontamento' },
    ],
  },
  {
    id: 'planejamento',
    labelKey: 'nav.grupoPlanejamento',
    section: 'management',
    items: [
      { icon: 'list_alt', textKey: 'nav.ops', to: '/ops' },
      { icon: 'grid_view', textKey: 'nav.mapa', to: '/mapa' },
      { icon: 'calendar_clock', textKey: 'nav.simulador', to: '/simulador' },
      { icon: 'layers', textKey: 'nav.balanceamento', to: '/balanceamento' },
      // UM item para as tres telas do tempo por peca: Curadoria, Sequencias e
      // Cronometragem sao abas de `/tempos`, porque elas sao um caminho so.
      { icon: 'timer', textKey: 'nav.tempos', to: '/tempos' },
    ],
  },
  {
    id: 'gestao',
    labelKey: 'nav.grupoGestao',
    section: 'registration',
    items: [
      { icon: 'monitoring', textKey: 'nav.relatorios', to: '/relatorios' },
      { icon: 'storefront', textKey: 'nav.portal', to: '/portal' },
    ],
  },
];

/**
 * The foot of the sidebar, below every group and above the sign out row: what is set up
 * once and the manual, which is not a subject of the product but how to use all of them.
 * These rows carry NO heading, and that is the point: they stay out of the day's reading
 * without spending one of the three headings the sidebar can show.
 */
export const NAVIGATION_FOOTER: ReadonlyArray<INavigationEntry> = [
  { icon: 'database', textKey: 'nav.cadastros', to: '/cadastros' },
  { icon: 'settings', textKey: 'nav.settings', to: '/settings' },
  { icon: 'help', textKey: 'nav.ajuda', to: '/ajuda' },
];
